/* eslint-disable @typescript-eslint/no-magic-numbers */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Constants from '@/Constants';
import { useGetCertificates } from '@/hooks/api/useCourseMode';
import {
  useGetAtHomeAccessCode,
  useGetLeaderboard,
  useGetStudentDetails,
  useGetStudentInfo,
  useGetStudentMonsterCards,
  useGetStudentSubjectCards,
  useSubmitStudentFeedback,
} from '@/hooks/api/useStudentAuth';
import { useSnackbar } from '@/hooks/useSnackbar';
import logger from '@/logger';
import {
  GenericApiResponse,
  GetAtHomeAccessCodeResponse,
  LeaderboardEntry,
  StudentFeedbackResponse,
} from '@/services/api';
import useStore from '@/store';
import { getDeviceInfo } from '@/utils';
import { getSessionItem, isApiResponseSuccess, isDesktop } from '@/utils/helper';



const useStudentProfile = (activeMenu?: string) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['adminLogin', 'studentProfile']);
  const { data: studentInfo } = useGetStudentInfo();
  const { data: studentProgramDetails } = useGetStudentDetails();
  const { data: leaderboardData } = useGetLeaderboard(activeMenu === 'Leaderboard');
  const { data: certificatesData } = useGetCertificates(activeMenu === 'Certificates');
  const { data: studentMonsterCardsData } = useGetStudentMonsterCards(
    activeMenu === t('studentProfile:monsterCards'),
  );
  const { data: studentSubjectCardsData } = useGetStudentSubjectCards(
    activeMenu === t('studentProfile:subjectCards'),
  );
  const myStudentId = getSessionItem('studentId');
  const { appVersion } = useStore();

  const [accessCode, setAccessCode] = useState<GetAtHomeAccessCodeResponse>();

  const { mutate: getAccessCode } = useGetAtHomeAccessCode({
    onSuccess: (data: GenericApiResponse<GetAtHomeAccessCodeResponse>) => {
      if (data.data.accessCode) {
        setAccessCode(data.data);
        localStorage.setItem('atHomeAccessCode', JSON.stringify(data.data));
      }
    },
  });

  const handleGetAccessCode = useCallback(() => {
    getAccessCode({ accessLocation: 'school' });
  }, [getAccessCode]);

  useEffect(() => {
    if (
      activeMenu === t('studentProfile:monsterCards') &&
      studentMonsterCardsData &&
      studentMonsterCardsData.monsterCardList.length === 0
    ) {
      showSnackbar(t('studentProfile:cardNotCollected'));
    }
  }, [activeMenu, studentMonsterCardsData, showSnackbar, t]);

  useEffect(() => {
    if (
      activeMenu === t('studentProfile:subjectCards') &&
      studentSubjectCardsData &&
      studentSubjectCardsData.subjectCardList.length === 0
    ) {
      showSnackbar(t('studentProfile:cardNotCollected'));
    }
  }, [activeMenu, studentSubjectCardsData, showSnackbar, t]);

  const monsterCards = useMemo(() => {
    if (!studentMonsterCardsData?.monsterCardList) return [];
    return studentMonsterCardsData.monsterCardList.map((card) => ({
      id: card.loId,
      name: card.monsterName,
      thumbnail: card.monsterImage,
    }));
  }, [studentMonsterCardsData]);

  const subjectCards = useMemo(() => {
    if (!studentSubjectCardsData?.subjectCardList) return [];
    return studentSubjectCardsData.subjectCardList.map((card) => ({
      id: card.subjectId,
      name: card.name,
      thumbnail: card.thumbnail,
    }));
  }, [studentSubjectCardsData]);

  useEffect(() => {
    if (
      studentProgramDetails?.metadata?.splashScreenImage &&
      Object.keys(studentProgramDetails.metadata.splashScreenImage).length > 0
    ) {
      localStorage.setItem(
        'splashScreenImage',
        JSON.stringify(studentProgramDetails?.metadata?.splashScreenImage),
      );
    }
  }, [studentProgramDetails]);

  const { mutate: submitStudentFeedback } = useSubmitStudentFeedback({
    onSuccess: (payload: GenericApiResponse<StudentFeedbackResponse>) => {
      if (payload && isApiResponseSuccess(payload)) {
        showSnackbar(t('adminLogin:feedbackSubmitted'), 'success');
        if (payload?.data?.ticketId) {
          logger.info('Feedback submitted', payload?.data?.ticketId);
        }
      }
    },
  });

  const getFeedbackStaticData = useMemo(() => {
    const { currentOs, currentBrowser, currentModel } = getDeviceInfo();
    const getDeviceId = () => {
      return localStorage.getItem('device-id') ?? '';
    };
    const getDeviceMetadata = () => {
      if (isDesktop()) {
        return {
          model: currentModel,
          operatingSystem: currentOs,
        };
      }
      return {
        model: currentModel,
        operatingSystem: currentOs,
        browser: currentBrowser,
      };
    };
    const getAdminId = () => {
      return localStorage.getItem('adminId') ?? '';
    };
    const getCurrentVersion = () => {
      return `${appVersion}/${Constants.GIT_INFO}`;
    };

    const getPlatform = () => {
      if (isDesktop()) {
        return 'desktop';
      }
      return 'web';
    };
    return {
      getDeviceId,
      getDeviceMetadata,
      getCurrentVersion,
      getPlatform,
      getAdminId,
    };
  }, [appVersion]);

  // Leaderboard derived state
  const leaderboardList: LeaderboardEntry[] = leaderboardData?.leaderboard ?? [];
  const currentUser = leaderboardData?.currentUser;

  const listWithoutCurrentUser = leaderboardList.filter((entry) => entry.studentId !== myStudentId);
  const currentUserEntry: LeaderboardEntry | undefined =
    currentUser && currentUser.rank
      ? { ...currentUser, studentId: myStudentId as string }
      : undefined;
  const mergedList: LeaderboardEntry[] = currentUserEntry
    ? [currentUserEntry, ...listWithoutCurrentUser]
    : listWithoutCurrentUser;

  const rankGroups: Record<number, LeaderboardEntry[]> = {};
  for (const entry of mergedList) {
    if (!rankGroups[entry.rank]) rankGroups[entry.rank] = [];
    rankGroups[entry.rank].push(entry);
  }

  const top3Ranks: number[] = Object.keys(rankGroups)
    .map(Number)
    .sort((a, b) => a - b)
    .slice(0, 3);

  const getPodiumName = (entries: LeaderboardEntry[]): string => {
    const current = entries.find((entry) => entry.studentId === myStudentId);
    const primary = current ?? entries[0];
    if (!primary) return '';
    const othersCount = entries.length - 1;
    return othersCount > 0
      ? `${primary.name} & ${othersCount} other${othersCount > 1 ? 's' : ''}`
      : primary.name;
  };

  type PodiumEntry = { rank: number; name: string; coins: number };

  const buildPodiumEntry = (rankIndex: number): PodiumEntry | undefined => {
    const rank = top3Ranks[rankIndex];
    if (rank === undefined) return undefined;
    const group = rankGroups[rank];
    const first = group?.[0];
    if (!group || !first) return undefined;
    return { rank, name: getPodiumName(group), coins: first.coins };
  };

  const podium1 = buildPodiumEntry(0);
  const podium2 = buildPodiumEntry(1);
  const podium3 = buildPodiumEntry(2);

  const onEarnCoins = () => {
    navigate(-1);
  };

  const onSubmitFeedback = (content: string, selectedCategory: string, onSuccess?: () => void) => {
    const { getDeviceId, getDeviceMetadata, getPlatform, getAdminId, getCurrentVersion } =
      getFeedbackStaticData;
    const requestBody = {
      category: selectedCategory,
      description: content,
      adminId: getAdminId(),
      appVersion: getCurrentVersion(),
      deviceId: getDeviceId(),
      platform: getPlatform(),
      deviceMetadata: getDeviceMetadata(),
    };
    if (content) {
      submitStudentFeedback(requestBody, { onSuccess });
    }
  };

  const certificates = certificatesData?.certificates ?? [];

  return {
    studentInfo,
    onSubmitFeedback,
    studentProgramDetails,
    subjectCards,
    monsterCards,
    handleGetAccessCode,
    accessCode,
    mergedList,
    currentUser,
    currentUserStudentId: myStudentId,
    podium1,
    podium2,
    podium3,
    onEarnCoins,
    certificates,
  };
};

export default useStudentProfile;
